import { useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useIntl, FormattedMessage } from "react-intl";
import * as v from "valibot";
import Suspense from "@/common/Suspense";
import { useValibotForm, useValibotSearchParams } from "@/utils/validation";
import { useOnChangeEffect, useOnceEffect } from "@/utils/hooks";
import { Form } from "@/kit/ui/form";
import { schemaFactory, Schema } from "./schema";
import { Filters } from "./Filters";
import Submissions, { SubmissionsHandle } from "./Submissions";
import { CompetitionApprovalsFragment$key } from "./__generated__/CompetitionApprovalsFragment.graphql";

const Fragment = graphql`
  fragment CompetitionApprovalsFragment on Competition
  @argumentDefinitions(approvalFilter: { type: "ApprovalFilter" }) {
    viewerCanApprove: viewerCan(action: CREATE_PROJECT_VERSION_APPROVAL)
    viewerCanReadPrivateApproval: viewerCan(
      action: READ_COMPETITION_PRIVATE_APPROVAL
    )
    anySubmission: submissions(first: 1) {
      edges {
        cursor
      }
    }
    ...FiltersForCompetitionApprovalsFragment
    ...SubmissionsCompetitionApprovalsFragment
      @arguments(approvalFilter: $approvalFilter)
  }
`;

export interface CompetitionApprovalsProps {
  defaultFilters: v.InferInput<Schema>;
  competition: CompetitionApprovalsFragment$key;
}

export default function CompetitionApprovals(props: CompetitionApprovalsProps) {
  const intl = useIntl();
  const competition = useFragment(Fragment, props.competition);
  const submissionsRef = useRef<SubmissionsHandle>(null);
  const schema = schemaFactory(intl);
  const [_, setSearchParams] = useValibotSearchParams(schema);
  const filtersForm = useValibotForm(schema, {
    defaultValues: props.defaultFilters,
  });
  const filters = filtersForm.watch();

  useOnceEffect(() => {
    if (
      props.defaultFilters.approvalFilter == undefined &&
      competition.viewerCanApprove
    ) {
      filtersForm.setValue("approvalFilter", "AWAITING_MINE");
    }
  });

  useOnChangeEffect(filters, () => {
    if (submissionsRef.current) {
      const parsed = v.safeParse(schema, filters);
      if (parsed.success) {
        setSearchParams(filters);
        submissionsRef.current.refetch(parsed.output);
      }
    }
  });

  if (competition.anySubmission.edges.length <= 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold w-full font-poppins">
        <FormattedMessage defaultMessage="Submissions" />
      </h1>
      <Form form={filtersForm} className="flex flex-col gap-4">
        <Filters competition={competition} />
        <Suspense>
          <Submissions ref={submissionsRef} competition={competition} />
        </Suspense>
      </Form>
    </div>
  );
}
